import React from "react";
import { Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const move = (arr, i, j) => { const a = [...arr]; [a[i], a[j]] = [a[j], a[i]]; return a; };
const iconBtn = "h-9 w-9 shrink-0 inline-flex items-center justify-center rounded-md hover:bg-muted disabled:opacity-30 transition-colors";

function Block({ title, hint, children }) {
  return (
    <div className="bg-white border border-border rounded-lg p-5">
      <h3 className="font-heading font-bold text-navy">{title}</h3>
      <p className="text-xs text-muted-foreground mb-4">{hint}</p>
      {children}
    </div>
  );
}

function RowTools({ i, len, onChange, list, testid }) {
  return (
    <>
      <button className={iconBtn} disabled={i === 0} onClick={() => onChange(move(list, i, i - 1))} data-testid={`${testid}-up-${i}`}><ArrowUp size={15} /></button>
      <button className={iconBtn} disabled={i === len - 1} onClick={() => onChange(move(list, i, i + 1))} data-testid={`${testid}-down-${i}`}><ArrowDown size={15} /></button>
      <button className={`${iconBtn} text-crimson`} onClick={() => onChange(list.filter((_, j) => j !== i))} data-testid={`${testid}-remove-${i}`}><Trash2 size={15} /></button>
    </>
  );
}

export function ListEditor({ title, hint, items = [], onChange, placeholder, testid }) {
  const list = Array.isArray(items) ? items : [];
  return (
    <Block title={title} hint={hint}>
      <div className="space-y-2" data-testid={testid}>
        {list.map((v, i) => (
          <div key={i} className="flex items-center gap-1">
            <Input value={v} placeholder={placeholder} onChange={(e) => onChange(list.map((x, j) => (j === i ? e.target.value : x)))} data-testid={`${testid}-item-${i}`} />
            <RowTools i={i} len={list.length} list={list} onChange={onChange} testid={testid} />
          </div>
        ))}
        <Button size="sm" variant="ghost" className="text-crimson" onClick={() => onChange([...list, ""])} data-testid={`${testid}-add`}><Plus size={14} className="mr-1" /> Add item</Button>
      </div>
    </Block>
  );
}

export function FaqEditor({ items = [], onChange }) {
  const list = Array.isArray(items) ? items : [];
  const upd = (i, k, v) => onChange(list.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <Block title="FAQs" hint="Questions shown in the FAQs tab of the course page">
      <div className="space-y-3" data-testid="faqs">
        {list.map((f, i) => (
          <div key={i} className="flex gap-1 items-start border border-border rounded-md p-3">
            <div className="flex-1 space-y-2">
              <Input value={f.q || ""} placeholder="Question" onChange={(e) => upd(i, "q", e.target.value)} data-testid={`faqs-q-${i}`} />
              <Textarea rows={2} value={f.a || ""} placeholder="Answer" onChange={(e) => upd(i, "a", e.target.value)} data-testid={`faqs-a-${i}`} />
            </div>
            <RowTools i={i} len={list.length} list={list} onChange={onChange} testid="faqs" />
          </div>
        ))}
        <Button size="sm" variant="ghost" className="text-crimson" onClick={() => onChange([...list, { q: "", a: "" }])} data-testid="faqs-add"><Plus size={14} className="mr-1" /> Add FAQ</Button>
      </div>
    </Block>
  );
}

export function CourseExtras({ f, set }) {
  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="course-extras">
      <ListEditor title="What you'll learn" hint="Key outcomes shown at the top of the course page" items={f.what_you_learn} onChange={(v) => set("what_you_learn", v)} placeholder="e.g. Assign ICD-10-CM codes accurately" testid="learn" />
      <ListEditor title="Course Highlights" hint="Selling points shown in the overview" items={f.highlights} onChange={(v) => set("highlights", v)} placeholder="e.g. Downloadable notes" testid="highlights" />
      <ListEditor title="Requirements" hint="What learners need before starting" items={f.requirements} onChange={(v) => set("requirements", v)} placeholder="e.g. Basic English reading skills" testid="requirements" />
      <FaqEditor items={f.faqs} onChange={(v) => set("faqs", v)} />
    </div>
  );
}
